import type { LocalizedSpeechPreviewItem } from '@/shared/api/types/localized-speech-types'
import type { IPoiTranslation } from '@/shared/api/types/poi-types'
import { Volume2, VolumeX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LocalizedTranslationJobSection } from '@/features/localized-translation'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { ReasonType } from '@/shared/constants/translation-status'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'

interface PoiTranslationSectionProps {
  /** Danh sách bản dịch POI, bao gồm metadata audio, do form cha quản lý. */
  translations: IPoiTranslation[]
  /** Hàm trả về bản dịch POI mới sau khi user sửa, bật/tắt audio hoặc job nền hoàn tất. */
  onChange: (translations: IPoiTranslation[]) => void
  /** Báo cho form cha biết còn nội dung đã sửa nhưng chưa tạo lại bản dịch/audio. */
  onGenerationStateChange?: (hasPendingGeneration: boolean) => void
  /** Id POI hoặc draft id ổn định dùng để ghép job nền về đúng form. */
  entityId: string
  /** Tên hiển thị tùy chọn trong toast hoặc background jobs panel. */
  entityLabel?: string
  /** Đường dẫn tùy chọn để mở lại POI từ background jobs panel. */
  entityPath?: string
  /** Ngôn ngữ được chọn sẵn khi vào form từ list/filter. */
  initialActiveLanguage?: string
}

/**
 * Gộp dữ liệu preview name/description/audioContent cùng metadata audioUrl/hasAudio.
 */
function applyPoiPreview(translation: IPoiTranslation, preview: LocalizedSpeechPreviewItem) {
  const audioUrl = preview.speechUrl ?? preview.audioUrl
  return {
    ...translation,
    name: preview.fields['name'] ?? translation.name,
    description: preview.fields['description'] ?? translation.description,
    audioContent: preview.fields['audioContent'] ?? translation.audioContent,
    // * VI: language_result trả về giữa chừng chưa có audio; completed mới có speech_url nên ghi đè khi có.
    ...(audioUrl ? { audioUrl, hasAudio: true } : {}),
  }
}

/**
 * Lớp bọc cấu hình workflow dịch/tạo audio nền cho name/description/audio_content của POI,
 * gộp chung 1 card vì 1 job dịch chỉ chạy 1 ngôn ngữ nguồn sang nhiều ngôn ngữ đích cùng lúc.
 */
export function PoiTranslationSection({
  translations,
  onChange,
  onGenerationStateChange,
  entityId,
  entityLabel,
  entityPath,
  initialActiveLanguage,
}: PoiTranslationSectionProps) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const languages = translations.map((tr) => tr.language).filter(Boolean)

  return (
    <LocalizedTranslationJobSection<IPoiTranslation>
      languages={languages}
      translations={translations}
      onChange={onChange}
      scope={TRANSLATION_JOB_SCOPE.POI}
      entityId={entityId}
      {...(entityLabel ? { entityLabel } : {})}
      {...(entityPath ? { entityPath } : {})}
      {...(initialActiveLanguage ? { initialActiveLanguage } : {})}
      audioEnabled={ReasonType.TRANSLATE_AUDIO_AVAILABLE}
      speechField="audio_content"
      requiredMessage={t('poi.translationRequired')}
      {...(onGenerationStateChange ? { onGenerationStateChange } : {})}
      applyPreviewToTranslation={applyPoiPreview}
      fields={(activeLanguage) => [
        {
          key: 'name',
          label: t('poi.name'),
          placeholder: t('poi.translationNamePlaceholder', {
            language: languageLabel(activeLanguage),
          }),
        },
        {
          key: 'description',
          label: t('poi.description'),
          placeholder: t('poi.translationDescriptionPlaceholder', {
            language: languageLabel(activeLanguage),
          }),
          multiline: true,
          rows: 5,
        },
        {
          key: 'audioContent',
          label: t('poi.audioContent'),
          placeholder: t('poi.translationAudioContentPlaceholder', {
            language: languageLabel(activeLanguage),
          }),
          multiline: true,
          rows: 5,
        },
      ]}
      translateLabel={t('poi.localizedSpeech.generateSelected')}
      scopeHint={t('poi.localizedSpeech.scopeHint')}
      loadingText={t('poi.localizedSpeech.generating')}
      renderExtraContent={({ currentTranslation, updateCurrent }) => (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-sm">
            {currentTranslation.hasAudio ? (
              <span className="flex items-center gap-1.5 text-green-600">
                <Volume2 size={14} />
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-zinc-400">
                <VolumeX size={14} />
              </span>
            )}
          </div>
          {currentTranslation.audioUrl && (
            <div className="space-y-1.5">
              <Label>{t('poi.audioGuide')}</Label>
              <audio controls src={currentTranslation.audioUrl} className="w-full" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch
              checked={currentTranslation.hasAudio ?? false}
              onCheckedChange={(v) => updateCurrent({ hasAudio: v })}
            />
            <Label>{t('poi.localizedSpeech.publishAudioForLanguage')}</Label>
          </div>
        </div>
      )}
    />
  )
}
